import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { UPDATE_CAR, UPDATE_CAR_HOURLY_PRICING } from '../../../services/carService/queries';
import { useUpdateEditCarStoreHook } from '../../../hooks/useUpdateEditCarStoreHook';
import { useSnapshot } from 'valtio';
import { carStore } from '../../../../store/carStore';
import { useBrandRules, useHostCapabilities } from '../../../hooks/useHostCapabilities';
import { getBrand } from '../../../constants/brands';

const Container = styled.div`
  ${tw`max-w-lg mx-auto p-6 flex flex-col items-center justify-center`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold mb-8`}
`;

const FormGrid = styled.div`
  ${tw`grid grid-cols-1 gap-4 w-full`}
`;

const FormGroup = styled.div`
  ${tw`mb-4 w-full`}
`;

const Label = styled.label`
  ${tw`block text-text-secondary font-medium mb-2`}
`;

const InputContainer = styled.div`
  ${tw`w-full mx-auto mb-4`}
`;

const StyledInput = styled.input`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  box-sizing: border-box;
`;

const NextButton = styled.button<{ isActivated: boolean }>`
  ${tw`w-full text-white py-3 rounded-lg font-semibold transition-colors shadow-xs`}
  ${({ isActivated }) =>
    isActivated
      ? css`
          ${tw`bg-black`}

          &:hover {
            ${tw`bg-black-hover`}
          }
        `
      : tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`}
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-center w-full mt-6`}
`;

const RequiredIndicator = styled.span`
  ${tw`text-red-500 ml-1`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-6`}
`;

const StepHint = styled.p`
  ${tw`text-sm text-text-secondary mb-6 max-w-md text-center`}
`;

export default function AddPricePage() {
  const navigate = useNavigate();
  const { editingCar, successfullUpdatedEditingCarId } = useUpdateEditCarStoreHook();
  const carStoreSnapshot = useSnapshot(carStore);
  const currentEditingCar = editingCar || carStoreSnapshot.editingCar;
  // Spec 014a: with `carPricing` off the brand uses one daily rate for every day.
  // Spec 014b: one daily rate when the brand says so (FR-018); hourly prices
  // only when the brand offers hourly rentals and leaves them to hosts.
  const { can: canHost } = useHostCapabilities();
  const { uniformDailyRate: uniformRate, hourlyRentalOffered } = useBrandRules();
  const askHourly = hourlyRentalOffered && canHost('carHourlyPricing');
  const [weekdayPrice, setWeekdayPrice] = useState('');
  const [weekendPrice, setWeekendPrice] = useState('');
  const [holidayPrice, setHolidayPrice] = useState('');
  const [hourlyRentalIsOn, setHourlyRentalIsOn] = useState(false);
  const [weekdayHrPrice, setWeekdayHrPrice] = useState('');
  const [weekendHrPrice, setWeekendHrPrice] = useState('');
  const [holidayHrPrice, setHolidayHrPrice] = useState('');

  const [updateCar] = useMutation(UPDATE_CAR);
  const [updateHourlyPricing] = useMutation(UPDATE_CAR_HOURLY_PRICING);

  useEffect(() => {
    if (currentEditingCar) {
      setWeekdayPrice(currentEditingCar.weekdayPrice?.toString() || '');
      setWeekendPrice(currentEditingCar.weekendPrice?.toString() || '');
      setHolidayPrice(currentEditingCar.holidayPrice?.toString() || '');
      const ec: any = currentEditingCar as any;
      setHourlyRentalIsOn(!!ec.hourlyRentalIsOn);
      setWeekdayHrPrice(ec.weekdayHrPrice != null ? String(ec.weekdayHrPrice) : '');
      setWeekendHrPrice(ec.weekendHrPrice != null ? String(ec.weekendHrPrice) : '');
      setHolidayHrPrice(ec.holidayHrPrice != null ? String(ec.holidayHrPrice) : '');
    }
  }, [currentEditingCar]);

  const handlePriceChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.replace(/[^0-9.]/g, '');
      setter(value);
    };

  const onSubmitPrices = async () => {
    try {
      if (!successfullUpdatedEditingCarId) {
        return;
      }

      if (!currentEditingCar?.id) {
        navigate('/cars/host/add');
        return;
      }

      if (uniformRate) {
        const dailyRate = parseFloat(weekdayPrice);
        if (!weekdayPrice || isNaN(dailyRate)) {
          return;
        }
        // The server accepts weekend and holiday prices equal to the daily rate.
        await updateCar({
          variables: {
            id: currentEditingCar.id,
            updateCarInput: {
              weekdayPrice: dailyRate,
              weekendPrice: dailyRate,
              holidayPrice: dailyRate,
            },
          },
        });
      } else {
        if (!weekdayPrice || !weekendPrice || !holidayPrice) {
          return;
        }
        if (
          isNaN(parseFloat(weekdayPrice)) ||
          isNaN(parseFloat(weekendPrice)) ||
          isNaN(parseFloat(holidayPrice))
        ) {
          return;
        }
        await updateCar({
          variables: {
            id: currentEditingCar.id,
            updateCarInput: {
              weekdayPrice: parseFloat(weekdayPrice),
              weekendPrice: parseFloat(weekendPrice),
              holidayPrice: parseFloat(holidayPrice),
            },
          },
        });
      }
      if (!askHourly) {
        // The monthly step forwards to photos when the brand sets monthly pricing.
        navigate('/cars/host/add-monthly-price');
        return;
      }
      await updateHourlyPricing({
        variables: {
          carId: currentEditingCar.id,
          input: {
            hourlyRentalIsOn,
            weekdayHrPrice: weekdayHrPrice ? parseFloat(weekdayHrPrice) : undefined,
            weekendHrPrice: weekendHrPrice ? parseFloat(weekendHrPrice) : undefined,
            holidayHrPrice: holidayHrPrice ? parseFloat(holidayHrPrice) : undefined,
          },
        },
      });

      navigate('/cars/host/add-monthly-price');
    } catch (error) {}
  };

  const isFormValid = uniformRate
    ? !!weekdayPrice
    : !!weekdayPrice && !!weekendPrice && !!holidayPrice;

  if (!successfullUpdatedEditingCarId) {
    return (
      <Container>
        <Title>Loading...</Title>
      </Container>
    );
  }

  if (!currentEditingCar?.id) {
    return (
      <Container>
        <Title>Car not found</Title>
        <Description>Please go back and start the listing process again.</Description>
        <ButtonContainer>
          <NextButton isActivated onClick={() => navigate('/cars/host/add')}>
            Go Back
          </NextButton>
        </ButtonContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Set Daily Car Pricing</Title>
      <Description>
        {uniformRate
          ? `Set your daily rate. ${getBrand().name} uses one rate for every day of the week.`
          : askHourly
            ? 'Set weekday, weekend, and holiday daily rates. Optional hourly rates are below.'
            : 'Set weekday, weekend, and holiday daily rates.'}
      </Description>
      <StepHint>
        {canHost('carMonthlyPricing')
          ? 'After this step you will set monthly pricing (optional) before uploading photos.'
          : 'After this step you will upload photos.'}
      </StepHint>

      <FormGrid>
        <FormGroup>
          <Label>
            {uniformRate ? 'Daily Price ($)' : 'Weekday Price ($)'}
            <RequiredIndicator>*</RequiredIndicator>
          </Label>
          <InputContainer>
            <StyledInput
              type="text"
              value={weekdayPrice}
              placeholder="Enter weekday price"
              onChange={handlePriceChange(setWeekdayPrice)}
              data-testid="car-weekday-price-input"
            />
          </InputContainer>
        </FormGroup>

        {!uniformRate && (
          <>
            <FormGroup>
              <Label>
                Weekend Price ($)<RequiredIndicator>*</RequiredIndicator>
              </Label>
              <InputContainer>
                <StyledInput
                  type="text"
                  value={weekendPrice}
                  placeholder="Enter weekend price"
                  onChange={handlePriceChange(setWeekendPrice)}
                  data-testid="car-weekend-price-input"
                />
              </InputContainer>
            </FormGroup>

            <FormGroup>
              <Label>
                Holiday Price ($)<RequiredIndicator>*</RequiredIndicator>
              </Label>
              <InputContainer>
                <StyledInput
                  type="text"
                  value={holidayPrice}
                  placeholder="Enter holiday price"
                  onChange={handlePriceChange(setHolidayPrice)}
                  data-testid="car-holiday-price-input"
                />
              </InputContainer>
            </FormGroup>
          </>
        )}

        {askHourly && (
          <>
            <FormGroup>
              <Label>Enable hourly rental</Label>
              <InputContainer>
                <input
                  type="checkbox"
                  checked={hourlyRentalIsOn}
                  onChange={e => setHourlyRentalIsOn(e.target.checked)}
                />
              </InputContainer>
            </FormGroup>

            <FormGroup>
              <Label>Weekday Hour Price ($)</Label>
              <InputContainer>
                <StyledInput
                  type="text"
                  value={weekdayHrPrice}
                  placeholder="Enter weekday hourly price"
                  onChange={handlePriceChange(setWeekdayHrPrice)}
                />
              </InputContainer>
            </FormGroup>

            <FormGroup>
              <Label>Weekend Hour Price ($)</Label>
              <InputContainer>
                <StyledInput
                  type="text"
                  value={weekendHrPrice}
                  placeholder="Enter weekend hourly price"
                  onChange={handlePriceChange(setWeekendHrPrice)}
                />
              </InputContainer>
            </FormGroup>

            <FormGroup>
              <Label>Holiday Hour Price ($)</Label>
              <InputContainer>
                <StyledInput
                  type="text"
                  value={holidayHrPrice}
                  placeholder="Enter holiday hourly price"
                  onChange={handlePriceChange(setHolidayHrPrice)}
                />
              </InputContainer>
            </FormGroup>
          </>
        )}
      </FormGrid>

      <ButtonContainer>
        <NextButton
          onClick={onSubmitPrices}
          isActivated={isFormValid}
          disabled={!isFormValid}
          data-testid="car-price-next"
        >
          Next
        </NextButton>
      </ButtonContainer>
    </Container>
  );
}
